import { addDays, type ISODate } from "./date";
import type { CalendarDefinition } from "./calendar";

/** One day inside a reusable holiday calendar. */
export interface HolidayCatalogDay {
  date: ISODate;
  name: string;
}

/** A named set the plan can turn on: a country's public holidays, a tournament, a fictional event. */
export interface HolidayCatalog {
  id: string;
  label: string;
  description: string;
  days: readonly HolidayCatalogDay[];
}

/** A holiday the plan added itself. `enabled` false keeps the row and drops it from the schedule. */
export interface UserHoliday {
  id: string;
  start: ISODate;
  end: ISODate;
  name: string;
  note?: string;
  enabled: boolean;
}

/** An official day turned off. The calendar stays selected; only this date is skipped. */
export interface HolidaySuppression {
  catalogId: string;
  date: ISODate;
}

const KR_DAYS: readonly HolidayCatalogDay[] = [
  { date: "2026-01-01", name: "신정" },
  { date: "2026-02-16", name: "설날 연휴" },
  { date: "2026-02-17", name: "설날" },
  { date: "2026-02-18", name: "설날 연휴" },
  { date: "2026-03-01", name: "삼일절" },
  { date: "2026-03-02", name: "대체공휴일" },
  { date: "2026-05-05", name: "어린이날" },
  { date: "2026-05-24", name: "부처님오신날" },
  { date: "2026-05-25", name: "대체공휴일" },
  { date: "2026-06-03", name: "지방선거" },
  { date: "2026-06-06", name: "현충일" },
  { date: "2026-08-15", name: "광복절" },
  { date: "2026-08-17", name: "대체공휴일" },
  { date: "2026-09-24", name: "추석 연휴" },
  { date: "2026-09-25", name: "추석" },
  { date: "2026-09-26", name: "추석 연휴" },
  { date: "2026-10-03", name: "개천절" },
  { date: "2026-10-05", name: "대체공휴일" },
  { date: "2026-10-09", name: "한글날" },
  { date: "2026-12-25", name: "성탄절" },
  { date: "2027-01-01", name: "신정" },
  { date: "2027-02-06", name: "설날 연휴" },
  { date: "2027-02-07", name: "설날" },
  { date: "2027-02-08", name: "설날 연휴" },
  { date: "2027-02-09", name: "대체공휴일" },
  { date: "2027-03-01", name: "삼일절" },
  { date: "2027-05-05", name: "어린이날" },
  { date: "2027-05-13", name: "부처님오신날" },
  { date: "2027-06-06", name: "현충일" },
  { date: "2027-08-15", name: "광복절" },
  { date: "2027-08-16", name: "대체공휴일" },
  { date: "2027-09-14", name: "추석 연휴" },
  { date: "2027-09-15", name: "추석" },
  { date: "2027-09-16", name: "추석 연휴" },
  { date: "2027-10-03", name: "개천절" },
  { date: "2027-10-04", name: "대체공휴일" },
  { date: "2027-10-09", name: "한글날" },
  { date: "2027-10-11", name: "대체공휴일" },
  { date: "2027-12-25", name: "성탄절" },
];

/** Calendars a plan can opt into. Official sets and a few example events live here. */
export const HOLIDAY_CATALOGS: readonly HolidayCatalog[] = [
  {
    id: "kr",
    label: "대한민국 공휴일",
    description: "2026–2027년 법정 공휴일과 대체공휴일",
    days: KR_DAYS,
  },
  {
    id: "worldcup-2026",
    label: "2026 월드컵",
    description: "개막일과 결승일을 쉬는 날로 둔 예시",
    days: [
      { date: "2026-06-11", name: "월드컵 개막" },
      { date: "2026-07-19", name: "월드컵 결승" },
    ],
  },
  {
    id: "company-event",
    label: "사내 행사",
    description: "창립기념일과 워크숍처럼 플랜에서 고르는 가상 행사",
    days: [
      { date: "2026-04-07", name: "창립기념일" },
      { date: "2026-11-20", name: "워크숍" },
      { date: "2027-04-07", name: "창립기념일" },
    ],
  },
];

const CATALOG_BY_ID = new Map(HOLIDAY_CATALOGS.map((catalog) => [catalog.id, catalog]));

export function holidayCatalog(id: string): HolidayCatalog | undefined {
  return CATALOG_BY_ID.get(id);
}

export function catalogDates(id: string, year?: number): ISODate[] {
  const catalog = CATALOG_BY_ID.get(id);
  if (!catalog) return [];
  return catalog.days.filter((day) => year == null || day.date.startsWith(`${year}-`)).map((day) => day.date);
}

export const KR_HOLIDAYS_2026: ISODate[] = catalogDates("kr", 2026);
export const KR_HOLIDAYS_2027: ISODate[] = catalogDates("kr", 2027);

/** Name from any catalog, used when the caller has no plan calendar. */
export function catalogHolidayName(iso: ISODate): string | undefined {
  for (const catalog of HOLIDAY_CATALOGS) {
    const hit = catalog.days.find((day) => day.date === iso);
    if (hit) return hit.name;
  }
  return undefined;
}

export function eachHolidayDate(start: ISODate, end: ISODate): ISODate[] {
  if (!start || !end) return [];
  const [from, to] = start <= end ? [start, end] : [end, start];
  const out: ISODate[] = [];
  for (let date = from; date <= to; date = addDays(date, 1)) out.push(date);
  return out;
}

function suppressionKey(catalogId: string, date: ISODate): string {
  return `${catalogId}:${date}`;
}

/** Dates that actually remove a working day. Disabled rows are omitted. */
export function effectiveHolidayDates(def: Pick<CalendarDefinition, "holidayCatalogs" | "holidaySuppressions" | "userHolidays">): ISODate[] {
  const suppressed = new Set((def.holidaySuppressions ?? []).map((item) => suppressionKey(item.catalogId, item.date)));
  const out = new Set<ISODate>();
  for (const id of def.holidayCatalogs ?? []) {
    const catalog = CATALOG_BY_ID.get(id);
    if (!catalog) continue;
    for (const day of catalog.days) {
      if (!suppressed.has(suppressionKey(id, day.date))) out.add(day.date);
    }
  }
  for (const item of def.userHolidays ?? []) {
    if (!item.enabled) continue;
    for (const date of eachHolidayDate(item.start, item.end)) out.add(date);
  }
  return [...out].sort();
}

/**
 * A flat `holidays` list from before catalogs existed.
 * The schedule stays the same: matching official days select that calendar,
 * missing official days become 사용 안 함, and the rest become added holidays.
 */
export function migrateLegacyHolidays(def: CalendarDefinition): CalendarDefinition {
  if (def.holidayCatalogs) return def;
  const dates = new Set(def.holidays ?? []);
  const selected: string[] = [];
  const suppressions: HolidaySuppression[] = [];
  const covered = new Set<ISODate>();
  for (const catalog of HOLIDAY_CATALOGS) {
    const hits = catalog.days.filter((day) => dates.has(day.date));
    const select = catalog.id === "kr" ? hits.length > 0 : hits.length > 0 && hits.length === catalog.days.length;
    if (!select) continue;
    selected.push(catalog.id);
    for (const day of catalog.days) {
      if (dates.has(day.date)) covered.add(day.date);
      else suppressions.push({ catalogId: catalog.id, date: day.date });
    }
  }
  const userHolidays: UserHoliday[] = [...dates]
    .filter((date) => !covered.has(date))
    .sort()
    .map((date) => ({
      id: `legacy-${date}`,
      start: date,
      end: date,
      name: catalogHolidayName(date) ?? "추가 공휴일",
      enabled: true,
    }));
  return {
    ...def,
    holidayCatalogs: selected,
    holidaySuppressions: suppressions,
    userHolidays,
  };
}

/** Fill `holidays` from the selected calendars, suppressions, and enabled additions. */
export function resolveCalendar(def: CalendarDefinition): CalendarDefinition {
  const base = def.holidayCatalogs ? def : migrateLegacyHolidays(def);
  const holidays = effectiveHolidayDates(base);
  const same = holidays.length === base.holidays.length && holidays.every((date, index) => date === base.holidays[index]);
  if (same) return base;
  return { ...base, holidays };
}

/** Select the Korean public-holiday calendar and keep anything the base already had. */
export function calendarWithKoreanHolidays(base: CalendarDefinition = { workingDays: [false, true, true, true, true, true, false], holidays: [] }): CalendarDefinition {
  const migrated = resolveCalendar(base);
  const catalogs = new Set(migrated.holidayCatalogs ?? []);
  catalogs.add("kr");
  return resolveCalendar({ ...migrated, holidayCatalogs: [...catalogs] });
}

/**
 * Name shown on a calendar day. With a plan calendar, only days that affect the schedule.
 * Without one, the first catalog that defines the date.
 */
export function holidayDisplayName(iso: ISODate, calendar?: CalendarDefinition): string | undefined {
  if (!calendar) return catalogHolidayName(iso);
  const resolved = calendar.holidayCatalogs ? calendar : migrateLegacyHolidays(calendar);
  const user = (resolved.userHolidays ?? []).find((item) => item.enabled && iso >= item.start && iso <= item.end);
  if (user?.name) return user.name;
  const suppressed = new Set((resolved.holidaySuppressions ?? []).map((item) => suppressionKey(item.catalogId, item.date)));
  for (const id of resolved.holidayCatalogs ?? []) {
    if (suppressed.has(suppressionKey(id, iso))) continue;
    const hit = CATALOG_BY_ID.get(id)?.days.find((day) => day.date === iso);
    if (hit) return hit.name;
  }
  return undefined;
}

export type HolidayDateConflict = "catalog" | "user";

/** A date the plan already tracks, including a day that is turned off. */
export function holidayDateConflict(def: CalendarDefinition, date: ISODate): HolidayDateConflict | null {
  const resolved = def.holidayCatalogs ? def : migrateLegacyHolidays(def);
  for (const item of resolved.userHolidays ?? []) {
    if (date >= item.start && date <= item.end) return "user";
  }
  for (const id of resolved.holidayCatalogs ?? []) {
    if (CATALOG_BY_ID.get(id)?.days.some((day) => day.date === date)) return "catalog";
  }
  return null;
}
